import { createSlice } from '@reduxjs/toolkit';

// ──────────────────────────────────────────────────────────────────────────────
// v6 state shape — matches Docs/Merchant_Onboarding_v6_Spec.md §3
//
// Backward-compat: every v5 reducer name is preserved. New v6 reducers are
// appended below the legacy block. Step1_SetupKYC and the existing wizard pages
// continue to work unchanged.
// ──────────────────────────────────────────────────────────────────────────────

const emptyModes = {
  surface: { reg: false, large: false },
  air: { reg: false, large: false },
  sdd: { reg: false, large: false },
  sc_ndd: { reg: false, large: false },
  intercity_ndd: { reg: false, large: false },
  h2to4: { reg: false, large: false },
  sub2h: { reg: false, large: false },
  hv: { reg: false, large: false },
};

const initialState = {
  // v5 — preserved
  stage: 0,
  activeDemo: null,
  merchant: null,
  signup: {
    business_name: '', brand: '', email: '', contact: '',
    address: '', pincode: '', city: '', state: '', tnc: false,
  },
  profile: {
    directions: { forward: false, reverse_noqc: false, reverse_qc: false },
    payment: '',
    shipment_value: 'low', // 'low' | 'high' — high = avg shipment > ₹10,000 incl. GST
    modes: emptyModes,
  },
  resolvedServices: [],
  // Per-service-type account-code selection (Profile → Service Type drill-down).
  // Shape: { [serviceId]: [cpAccountCode, ...] }. An ABSENT entry means
  // "all active account codes selected" — the backward-compatible default that
  // keeps the pricing-upload template scoped to every code unless the analyst
  // narrows it down here.
  accountCodeSelection: {},
  agreement: {
    filename: '', pdfDataUrl: null, s3Key: null, s3Url: null, jobId: null, extractionStatus: null,
    gst_stance: 'exclusive', vc: 5000, weight_ceiling: 20000,
    detected_services: [], extraTcsChips: [],
    confidence: 0, confirmed: false,
    // v6.1+ — per-parent T&C buckets
    extracted: {
      rates: null, slabKeys: null,
      tcs_default: null,
      tcs_per_parent: {},
      tcs_blended_surface: null, tcs_blended_air: null, tcs_blended_sdd_ndd: null,
    },
  },
  artifacts: [],
  confirmations: {},
  drafted: false,

  // ── v6 additions ──────────────────────────────────────────────────────────
  merchantId: null,
  rateCardMode: 'courier_wise', // 'courier_wise' | 'blended'

  // Rate Card Modification MoM (Sep 2026): Commercials selectors that drive which
  // sheets the workbook template carries. FIXED → charge comes from T&C values;
  // VARIABLE → the COD / RTO sheet is included in the download and must be filled.
  codChargeMode: 'FIXED', // 'FIXED' | 'VARIABLE'
  rtoChargeMode: 'FIXED', // 'FIXED' | 'VARIABLE' (variable RTO is % of forward only)

  // Custom weight slabs (business ask, Sep 2026): some parents (e.g. DTDC) run
  // their own slab ladder. Selected parents move to "(Custom)" workbook sheets
  // whose ladder (Min/Max) is editable; ONE shared custom ladder for all of them.
  customSlabParents: { enabled: false, parents: [] },

  // KYC — full lifecycle. Field names match Merchant_Onboarding_v6.html.
  kyc: {
    overallStatus: 'not_started', // not_started | in_review | approved | rejected
    submittedAt: null,
    companyType: '',
    docs: {
      pan: { status: 'not_uploaded', number: '', file: null, verify: null },
      gstin: { status: 'not_uploaded', number: '', file: null, verify: null },
      msme: { status: 'not_uploaded', number: '', file: null, not_registered: false },
      bank: { status: 'not_uploaded', account_no: '', ifsc: '', bank_name: '', beneficiary_name: '', account_type: 'CURRENT', file: null },
      signatory: { status: 'not_uploaded', name: '', dob: '', aadhaar_last4: '', aadhaar_no: '', file: null },
      address_proof: { status: 'not_uploaded', type: 'utility', file: null },
    },
    additionalDocs: [], // [{ id, type, remark, file }]
  },

  // Master info — sign-up companion card
  masterInfo: {
    order_sources: [],
    category: '',
    pickup_address: { line1: '', line2: '', city: '', state: '', pincode: '', contact_name: '', contact_number: '', gstin: '', pickup_id: '' },
    aov: 0,
    units_per_order: 0,
    monthly_volume: 0,
    // Wallet share of the brand's overall volume given to Prozo (0–100%)
    prozo_wallet_share_pct: 100,
    // Proship integration / tech stack
    integration_type: '',        // 'storefront' | 'oms'
    storefront_platform: '',     // single-select when integration_type === 'storefront'
    oms_platforms: [],           // multi-select when integration_type === 'oms'
    custom_oms: [],              // free-text custom OMS names
  },

  // Payment & Credit — Stage 2 (Commercials)
  credit: {
    engagement: 'prepaid', // prepaid | postpaid | secured_postpaid
    credit_exposure: 0,
    credit_period_days: 30,
    cod_split_pct: 50,
    prepaid_split_pct: 50,
    avg_invoice_value: 0,
    recommended_tier: '',
    recommended_limit: 0,
    sales_override: '',
    approval_file: null,   // { name, type, size, dataUrl } — local preview only
    approval_s3Key: null,  // set after successful server upload
    secured_postpaid: {
      remit_days: 3,
      remit_freq_per_week: 2,
      fee_adjust_every: 3,
      adjust_window_days: 7,
    },
  },

  // Warehouse — Stage 1 (Profile)
  warehouse: {
    is_prozo: 'yes', // 'yes' | 'no' | 'mixed'
    prozo_warehouses: [],
    non_prozo: [], // [{ id, label, line1, line2, city, state, pincode }]
  },

  // Two-tier RTO — Stage 5 (Serviceability) + per-courier overrides
  rto: {
    mode: 'pct_fwd', // fixed | pct_fwd | slab
    fixed_amount: 0,
    pct_fwd: 70,
    slab: [],
    overrides: {}, // { [parentName]: { mode, fixed_amount, pct_fwd, slab } }
  },

  // Two-mode RVP — Stage 4 (RVP)
  rvp: {
    enabled: false,
    mode: 'forward_linked', // forward_linked (Mode A) | independent (Mode B)
    formulas: {}, // Mode A — { [parentOrBlended]: { type, flat, mult } }
    arrangement_mode: 'cp', // 'cp' | 'blended' (locked to global rateCardMode)
    slabPattern: 'p1',
    extracted: { rates: null, slabKeys: null, source: 'ai' },
    rows: [],
    completion: {},
    currentTab: 'forward',
    activeParent: null,
    tcs: {
      qc_applicable: false,
      qc_kind: 'flat',
      qc_amount: 0,
      courier_charges: [],
      fuel_pct: 0,
      vc: 5000,
      weight_ceiling: 20000,
      invoice_ceiling: 50000,
      attempts_included: 2,
      attempt_extra_fee: 0,
    },
  },

  // Per-service commercial arrangement — Stage 3 (Commercials) arrangement block.
  // { [serviceId]: { type: 'ZWZ'|'ZAW'|'ZAWA'|'WAZ', mode: 'cp'|'blended', ceiling } }
  arrangement: {},

  // Rate card builder — Stage 4. Rows are the 17-column ProShip schema, built by
  // rateEngine.buildRateCardRows() and stored here. Completion is per-account-code.
  rateCard: {
    rows: [],
    rowsSource: null, // null = engine-built; 'template' = from uploaded file (survives remount rebuilds)
    completion: {}, // { [account_code]: 'in-progress' | 'complete' }
    slabTemplate: 'proship-50kg',
    customSlabs: '250, 500, 500, 500',
    activeLane: 'surface', // blended mode: 'surface' | 'air'
    sortByStatus: false,
  },

  // Pre-flight + commit — Stages 7/8.
  preflight: null, // { ok, issues:[{acc,msg}] }
  commit: null, // { version, written, rows, ts, failures }
  effective_date: '',
  effective_mode: 'immediate', // 'immediate' | 'scheduled'
  invoice_ceiling: 50000,

  // T&Cs / Serviceability — Stage 5
  tc: {
    blended: {
      cod_structure: 'flat', // 'flat' | 'tiered_weight' | 'tiered_invoice'
      cod_fixed: 27,
      cod_pct: 1.25,
      cod_op: 'MAX',
      cod_def: 'MAX(₹27 fixed, 1.25% of COD value)',
      cod_tiers_weight: [{ min: 0, max: 500, charge: 25 }, { min: 500, max: 1000, charge: 35 }],
      cod_tiers_invoice: [{ min: 0, max: 500, charge: 0 }, { min: 500, max: 2000, charge: 25 }],
      rto_pct: 90,
      rvp_formula: 'flat_plus_fwd',
      rvp_flat: 25,
      rvp_pct: 0,
      qc: 25,
      remit: 'D+7',
      fuel_pct: 0,
      docket: 0,
      weight_ceiling: 20000,
      invoice_ceiling: 50000,
    },
    perCourier: {},
    blended_surface: {},
    blended_air: {},
    blended_sdd_ndd: {},
  },

  // DPH-Linked FSC (Diesel Price Hike) — agreement-scoped fuel-surcharge mechanism.
  // Captured in the T&Cs stage; D1 (monthly diesel price) is entered by Billing separately.
  dph: {
    enabled: false,
    base_price: 90, // ₹/L — variance is always computed from this
    trigger_enabled: false,
    trigger_price: null, // absolute ₹/L; DPH stays 0 until D1 ≥ this, then variance from base
    mechanism: 'percentage', // 'percentage' | 'rupee'
    hike_rate: 0.25, // unit depends on mechanism
    applicable: { forward: true, rto: true, rvp: true, cod: false, qc: false, vas: false },
  },

  // Freeze / unlock policy
  unlocks: {}, // { [stageId]: { by, at, reason } }
  editMode: false,
  amendments: [],
  lastSavedAt: null,
  pickupRegistered: false,
  pickupModified: false,
  kycUserModified: false,

  // AI badge state machine — tracks edits per field
  aiEdits: {}, // { [fieldId]: { original, current, confidence, edited } }

  // Live serviceability groups from API — replaces hardcoded PARENT_MAP
  serviceabilityGroups: [],
};

const pricingWizardSlice = createSlice({
  name: 'pricingWizard',
  initialState,
  reducers: {
    // ── v5 reducers (unchanged) ─────────────────────────────────────────────
    setStage(state, { payload }) { state.stage = payload; },
    nextStage(state) { if (state.stage < 8) state.stage += 1; },
    prevStage(state) { if (state.stage > 0) state.stage -= 1; },
    updateSignup(state, { payload }) { state.signup = { ...state.signup, ...payload }; },
    setMerchant(state, { payload }) { state.merchant = payload; },
    toggleDirection(state, { payload }) {
      state.profile.directions[payload] = !state.profile.directions[payload];
    },
    // Exact setter for hydration — see setModeEnabled. Toggling a direction that the
    // server already reported ON turns it OFF, which empties resolvedServices and makes
    // the Rate Card step claim no services are selected.
    setDirectionEnabled(state, { payload }) {
      const { direction, value } = payload;
      if (direction in state.profile.directions) state.profile.directions[direction] = !!value;
    },
    setPayment(state, { payload }) { state.profile.payment = payload; },
    setShipmentValue(state, { payload }) { state.profile.shipment_value = payload; },
    toggleMode(state, { payload }) {
      const { mode, size } = payload;
      state.profile.modes[mode][size] = !state.profile.modes[mode][size];
    },
    // Exact setter — hydration must not use toggleMode, which flips and is therefore
    // not idempotent: hydrating twice turned every mode back OFF, which re-derived
    // resolvedServices to [] and made the Rate Card step claim no services were selected.
    setModeEnabled(state, { payload }) {
      const { mode, size, value } = payload;
      if (state.profile.modes[mode]) state.profile.modes[mode][size] = !!value;
    },
    setResolvedServices(state, { payload }) { state.resolvedServices = payload; },
    toggleResolvedService(state, { payload }) {
      const exists = state.resolvedServices.find((r) => r.id === payload.id);
      if (exists) state.resolvedServices = state.resolvedServices.filter((r) => r.id !== payload.id);
      else state.resolvedServices.push(payload);
    },

    // ── Per-service account-code selection (Profile → Service Type) ───────────
    // `allCodes` carries the full active code list so the reducer can materialise
    // the implicit "all selected" default before flipping a single code.
    toggleServiceAccountCode(state, { payload }) {
      const { serviceId, code, allCodes } = payload;
      const base = state.accountCodeSelection[serviceId] || allCodes || [];
      const set = new Set(base);
      if (set.has(code)) set.delete(code); else set.add(code);
      state.accountCodeSelection[serviceId] = [...set];
    },
    setServiceParentCodes(state, { payload }) {
      // Select/clear every account code under one parent for a service.
      const { serviceId, parentCodes, allCodes, select } = payload;
      const base = state.accountCodeSelection[serviceId] || allCodes || [];
      const set = new Set(base);
      (parentCodes || []).forEach((c) => { if (select) set.add(c); else set.delete(c); });
      state.accountCodeSelection[serviceId] = [...set];
    },
    setServiceAccountCodes(state, { payload }) {
      const { serviceId, codes } = payload;
      state.accountCodeSelection[serviceId] = Array.isArray(codes) ? [...codes] : [];
    },
    setAgreement(state, { payload }) { state.agreement = { ...state.agreement, ...payload }; },
    setGstStance(state, { payload }) { state.agreement.gst_stance = payload; },
    confirmAgreement(state, { payload }) {
      state.agreement.confirmed = true;
      state.confirmations.commercial_construct = { by: payload || 'Admin', at: new Date().toISOString() };
    },
    loadDemo(state, { payload }) {
      const { key, demo } = payload;
      state.activeDemo = key;
      state.signup = { ...state.signup, ...demo.signup };
      state.profile = {
        directions: { ...state.profile.directions, ...demo.profile.directions },
        payment: demo.profile.payment,
        modes: { ...state.profile.modes, ...demo.profile.modes },
      };
      state.agreement = {
        ...state.agreement,
        filename: demo.agreement.filename,
        gst_stance: demo.agreement.gst_stance,
        vc: demo.agreement.vc,
        weight_ceiling: demo.agreement.weight_ceiling,
        detected_services: [...demo.agreement.detected_services],
        extraTcsChips: demo.extraTcsChips ? [...demo.extraTcsChips] : [],
      };
      if (demo.masterInfo) state.masterInfo = { ...state.masterInfo, ...demo.masterInfo };
    },
    registerArtifact(state, { payload }) {
      const { stage, name, note } = payload;
      if (!state.artifacts.find((a) => a.stage === stage && a.name === name)) {
        state.artifacts.push({ stage, name, note });
      }
    },
    resetWizard() { return initialState; },

    // ── v6 reducers ─────────────────────────────────────────────────────────
    setMerchantId(state, { payload }) { state.merchantId = payload; },
    setRateCardMode(state, { payload }) {
      state.rateCardMode = payload;
      state.rvp.arrangement_mode = payload === 'blended' ? 'blended' : 'cp';
    },
    setEffectiveDate(state, { payload }) { state.effective_date = payload; },

    // KYC
    updateKycDoc(state, { payload }) {
      const { docId, patch } = payload;
      state.kyc.docs[docId] = { ...state.kyc.docs[docId], ...patch };
    },
    submitKyc(state) {
      Object.keys(state.kyc.docs).forEach((k) => {
        if (state.kyc.docs[k].status === 'uploaded') state.kyc.docs[k].status = 'under_review';
      });
      state.kyc.overallStatus = 'in_review';
      state.kyc.submittedAt = new Date().toISOString();
    },
    setKycVerdict(state, { payload }) {
      // payload: 'approved' | 'rejected'
      state.kyc.overallStatus = payload;
      Object.keys(state.kyc.docs).forEach((k) => {
        if (state.kyc.docs[k].status === 'under_review') state.kyc.docs[k].status = payload;
      });
    },
    setKycCompanyType(state, { payload }) { state.kyc.companyType = payload; },
    addKycAdditionalDoc(state) {
      state.kyc.additionalDocs.push({ id: 'adoc-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6), type: '', remark: '', file: null });
    },
    updateKycAdditionalDoc(state, { payload }) {
      const { id, patch } = payload;
      const d = state.kyc.additionalDocs.find((x) => x.id === id);
      if (d) Object.assign(d, patch);
    },
    removeKycAdditionalDoc(state, { payload }) {
      state.kyc.additionalDocs = state.kyc.additionalDocs.filter((x) => x.id !== payload);
    },
    setAgreementS3Key(state, { payload }) { state.agreement.s3Key = payload; },
    setAgreementJobId(state, { payload }) { state.agreement.jobId = payload; },
    setExtractionStatus(state, { payload }) { state.agreement.extractionStatus = payload; },

    // Master info
    updateMasterInfo(state, { payload }) {
      state.masterInfo = { ...state.masterInfo, ...payload };
    },
    setPickupAddress(state, { payload }) {
      state.masterInfo.pickup_address = { ...state.masterInfo.pickup_address, ...payload };
    },

    // Credit
    updateCredit(state, { payload }) { state.credit = { ...state.credit, ...payload }; },
    setCreditApprovalS3Key(state, { payload }) { state.credit.approval_s3Key = payload; },
    updateSecuredPostpaid(state, { payload }) {
      state.credit.secured_postpaid = { ...state.credit.secured_postpaid, ...payload };
    },
    setCodSplit(state, { payload }) {
      const pct = Math.max(0, Math.min(100, payload));
      state.credit.cod_split_pct = pct;
      state.credit.prepaid_split_pct = 100 - pct;
    },

    // Warehouse
    updateWarehouse(state, { payload }) { state.warehouse = { ...state.warehouse, ...payload }; },
    toggleProzoWarehouse(state, { payload }) {
      const idx = state.warehouse.prozo_warehouses.indexOf(payload);
      if (idx >= 0) state.warehouse.prozo_warehouses.splice(idx, 1);
      else state.warehouse.prozo_warehouses.push(payload);
    },
    addNonProzoWarehouse(state, { payload }) {
      state.warehouse.non_prozo.push({
        id: 'wh-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6),
        label: '', name: '', contact_name: '', contact_number: '', gstin: '',
        line1: '', line2: '', city: '', state: '', pincode: '',
        ...payload,
      });
    },
    updateNonProzoWarehouse(state, { payload }) {
      const { id, patch } = payload;
      const wh = state.warehouse.non_prozo.find((w) => w.id === id);
      if (wh) Object.assign(wh, patch);
    },
    removeNonProzoWarehouse(state, { payload }) {
      state.warehouse.non_prozo = state.warehouse.non_prozo.filter((w) => w.id !== payload);
    },

    // RTO
    updateRto(state, { payload }) { state.rto = { ...state.rto, ...payload }; },
    setRtoOverride(state, { payload }) {
      const { parent, patch } = payload;
      state.rto.overrides[parent] = { ...(state.rto.overrides[parent] || {}), ...patch };
    },
    clearRtoOverride(state, { payload }) {
      delete state.rto.overrides[payload];
    },

    // RVP
    // 'cod' | 'rto' → 'FIXED' | 'VARIABLE' (Commercials stage selectors).
    setChargeMode(state, { payload }) {
      const { kind, mode } = payload;
      if (kind === 'cod') state.codChargeMode = mode === 'VARIABLE' ? 'VARIABLE' : 'FIXED';
      if (kind === 'rto') state.rtoChargeMode = mode === 'VARIABLE' ? 'VARIABLE' : 'FIXED';
    },
    // Custom-slab parents (Commercials stage): {enabled?, parents?} merge-patch.
    setCustomSlabParents(state, { payload }) {
      if (payload.enabled != null) state.customSlabParents.enabled = !!payload.enabled;
      if (payload.parents != null) state.customSlabParents.parents = payload.parents;
    },
    setRvpEnabled(state, { payload }) { state.rvp.enabled = payload; },
    setRvpMode(state, { payload }) { state.rvp.mode = payload; },
    updateRvpFormula(state, { payload }) {
      const { parent, patch } = payload;
      // Default type must match the dropdown's display default ('higher'), else editing
      // a field before picking a type would materialise type:'add' and snap the dropdown.
      state.rvp.formulas[parent] = { ...(state.rvp.formulas[parent] || { type: 'higher', flat: 0, mult: 1, qc: 0 }), ...patch };
    },
    setRvpSlabPattern(state, { payload }) { state.rvp.slabPattern = payload; state.rvp.extracted = { rates: null, slabKeys: null, source: 'ai' }; },
    updateRvpTcs(state, { payload }) { state.rvp.tcs = { ...state.rvp.tcs, ...payload }; },
    setRvpTab(state, { payload }) { state.rvp.currentTab = payload; },
    setRvpExtracted(state, { payload }) { state.rvp.extracted = payload; },
    updateRvpRate(state, { payload }) {
      // A cell holds { noqc, qc } (Mode B has separate nQC + QC reverse prices).
      // Legacy/uploaded cells may be a bare number (= nQC); migrate on first edit.
      const { parent, zone, slabKey, field = 'noqc', value } = payload;
      if (!state.rvp.extracted || !state.rvp.extracted.rates) return;
      const byParent = state.rvp.extracted.rates[parent];
      if (!byParent || !byParent[zone]) return;
      const cur = byParent[zone][slabKey];
      const obj = (cur && typeof cur === 'object')
        ? { ...cur }
        : { noqc: typeof cur === 'number' ? cur : null, qc: null };
      obj[field] = value;
      byParent[zone][slabKey] = obj;
    },

    // Edit one field on a reverse rate-card row (Mode B full-column matrix, per code).
    updateRvpRow(state, { payload }) {
      const { accountCode, parent, zone, start, key, value } = payload;
      const rows = state.rvp.extracted && state.rvp.extracted.rows;
      if (!Array.isArray(rows)) return;
      const row = rows.find((r) =>
        (accountCode ? r.account_code === accountCode : r.parent === parent)
        && r.zone === zone && r.start === start);
      if (row) row[key] = value;
    },

    // ── Rate card builder (Stage 4) ─────────────────────────────────────────
    setRateCardRows(state, { payload }) {
      const rows = Array.isArray(payload) ? payload : payload.rows;
      state.rateCard.rows = rows;
      state.rateCard.rowsSource = Array.isArray(payload) ? null : (payload.source || null);
      // Ensure every account code has a completion entry.
      [...new Set(rows.map((r) => r.account_code))].forEach((a) => {
        if (!state.rateCard.completion[a]) state.rateCard.completion[a] = 'in-progress';
      });
    },
    markRateCardCodes(state, { payload }) {
      const { codes, status } = payload;
      codes.forEach((c) => { state.rateCard.completion[c] = status; });
    },
    setSlabTemplate(state, { payload }) {
      state.rateCard.slabTemplate = payload.slabTemplate;
      if (payload.customSlabs != null) state.rateCard.customSlabs = payload.customSlabs;
      state.rateCard.rows = [];
      state.rateCard.rowsSource = null;
      state.rateCard.completion = {};
    },
    setActiveLane(state, { payload }) { state.rateCard.activeLane = payload; },
    toggleSortByStatus(state) { state.rateCard.sortByStatus = !state.rateCard.sortByStatus; },
    updateRateCardRow(state, { payload }) {
      const { account_code, zone, start, key, value } = payload;
      const row = state.rateCard.rows.find((r) => r.account_code === account_code && r.zone === zone && r.start === start);
      if (row) row[key] = value;
    },

    // ── Pre-flight / commit (Stages 7/8) ────────────────────────────────────
    setPreflight(state, { payload }) { state.preflight = payload; },
    setCommit(state, { payload }) { state.commit = payload; },
    setEffectiveMode(state, { payload }) { state.effective_mode = payload; },
    setInvoiceCeiling(state, { payload }) { state.invoice_ceiling = payload; },

    // ── COD tiers (Stage 5) ─────────────────────────────────────────────────
    setCodTiers(state, { payload }) {
      const { kind, tiers } = payload; // kind: 'weight' | 'invoice'
      if (kind === 'weight') state.tc.blended.cod_tiers_weight = tiers;
      else state.tc.blended.cod_tiers_invoice = tiers;
    },

    // ── RTO slab table (Stage 5) ────────────────────────────────────────────
    setRtoSlab(state, { payload }) { state.rto.slab = payload; },

    // ── Confirmations / audit ───────────────────────────────────────────────
    // payload can be a string key (legacy) or { key, by } for named confirmations
    recordConfirmation(state, { payload }) {
      const key = typeof payload === 'string' ? payload : payload.key;
      const by  = typeof payload === 'string' ? 'Admin' : (payload.by || 'Admin');
      state.confirmations[key] = { by, at: new Date().toISOString() };
    },
    clearConfirmation(state, { payload }) { delete state.confirmations[payload]; },

    // ── Weight-ceiling propagation (Stage 5 → arrangement + rebuild) ─────────
    setWeightCeiling(state, { payload }) {
      state.agreement.weight_ceiling = payload;
      if (state.agreement.extracted && state.agreement.extracted.tcs) state.agreement.extracted.tcs.weight_ceiling = payload;
      Object.keys(state.arrangement).forEach((svcId) => { if (state.arrangement[svcId]) state.arrangement[svcId].ceiling = payload; });
      state.rateCard.rows = [];
      state.rateCard.rowsSource = null;
      state.rateCard.completion = {};
    },

    // Arrangement (per-service)
    setArrangement(state, { payload }) {
      const { serviceId, patch } = payload;
      state.arrangement[serviceId] = {
        ...(state.arrangement[serviceId] || { type: 'ZWZ', mode: 'cp', ceiling: null }),
        ...patch,
      };
    },
    syncArrangementMode(state) {
      // Lock every per-service arrangement.mode to the global rateCardMode.
      const mode = state.rateCardMode === 'blended' ? 'blended' : 'cp';
      Object.keys(state.arrangement).forEach((k) => { state.arrangement[k].mode = mode; });
    },

    // T&Cs
    updateBlendedTc(state, { payload }) { state.tc.blended = { ...state.tc.blended, ...payload }; },
    setBlendedLaneTcs(state, { payload }) {
      // payload: { laneKey: 'blended_surface'|'blended_air'|'blended_sdd_ndd', tcs }
      state.tc[payload.laneKey] = payload.tcs || {};
    },

    // DPH-Linked FSC
    updateDph(state, { payload }) { state.dph = { ...state.dph, ...payload }; },
    toggleDphCharge(state, { payload }) {
      state.dph.applicable[payload] = !state.dph.applicable[payload];
    },
    updatePerCourierTc(state, { payload }) {
      const { parent, patch } = payload;
      state.tc.perCourier[parent] = { ...(state.tc.perCourier[parent] || {}), ...patch };
    },

    // Freeze / unlock
    addUnlock(state, { payload }) {
      const { stageId, by, reason } = payload;
      state.unlocks[stageId] = { by, at: new Date().toISOString(), reason };
    },
    clearUnlocks(state) { state.unlocks = {}; },
    setEditMode(state, { payload }) { state.editMode = payload; },
    addAmendment(state, { payload }) {
      state.amendments.push({
        version: state.amendments.length + 1,
        ts: new Date().toISOString(),
        ...payload,
      });
    },

    // AI badge
    trackAiEdit(state, { payload }) {
      const { fieldId, original, current, confidence } = payload;
      state.aiEdits[fieldId] = {
        original, current, confidence,
        edited: original !== current,
      };
    },
    setServiceabilityGroups(state, { payload }) { state.serviceabilityGroups = payload; },

    // Persistence
    setLastSaved(state) { state.lastSavedAt = new Date().toISOString(); },
    setPickupRegistered(state, { payload }) { state.pickupRegistered = payload; },
    setPickupModified(state, { payload }) { state.pickupModified = payload; },
    setKycUserModified(state, { payload }) { state.kycUserModified = payload; },

    // Hydration from localStorage
    hydrateWizard(state, { payload }) {
      // Replace state but keep current stage so user lands on what they were on.
      return { ...initialState, ...payload, stage: payload.stage ?? 0 };
    },
  },
});

export const {
  // v5
  setStage, nextStage, prevStage, updateSignup, setMerchant,
  toggleDirection, setPayment, setShipmentValue, toggleMode,
  setResolvedServices, toggleResolvedService, setModeEnabled, setDirectionEnabled,
  toggleServiceAccountCode, setServiceParentCodes, setServiceAccountCodes,
  setAgreement, setGstStance, confirmAgreement,
  loadDemo, registerArtifact, resetWizard,
  // v6
  setMerchantId, setRateCardMode, setEffectiveDate,
  updateKycDoc, submitKyc, setKycVerdict, setKycCompanyType,
  addKycAdditionalDoc, updateKycAdditionalDoc, removeKycAdditionalDoc,
  updateMasterInfo, setPickupAddress,
  updateCredit, updateSecuredPostpaid, setCodSplit, setCreditApprovalS3Key,
  updateWarehouse, toggleProzoWarehouse,
  addNonProzoWarehouse, updateNonProzoWarehouse, removeNonProzoWarehouse,
  updateRto, setRtoOverride, clearRtoOverride,
  setChargeMode,
  setCustomSlabParents,
  setRvpEnabled, setRvpMode, updateRvpFormula, setRvpSlabPattern, updateRvpTcs, setRvpTab,
  setRvpExtracted, updateRvpRate, updateRvpRow,
  setArrangement, syncArrangementMode,
  setRateCardRows, markRateCardCodes, setSlabTemplate, setActiveLane, toggleSortByStatus, updateRateCardRow,
  setPreflight, setCommit, setEffectiveMode, setInvoiceCeiling,
  setCodTiers, setRtoSlab, recordConfirmation, clearConfirmation, setWeightCeiling,
  updateBlendedTc, setBlendedLaneTcs, updatePerCourierTc,
  updateDph, toggleDphCharge,
  addUnlock, clearUnlocks, setEditMode, addAmendment,
  trackAiEdit, setLastSaved, setPickupRegistered, setPickupModified, setKycUserModified, hydrateWizard,
  setAgreementS3Key, setAgreementJobId, setExtractionStatus,
  setServiceabilityGroups,
} = pricingWizardSlice.actions;

export default pricingWizardSlice.reducer;
